// Settings: category management + user accounts. Rendered into any container.
import { api } from "../api";
import { renderCategorySettings } from "./categories";
import { confirmDialog, textPrompt } from "./prompt";
import { toast } from "./toast";

const esc = (s: string): string =>
  (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function renderSettingsInto(container: HTMLElement, currentUsername: string): void {
  container.innerHTML = `
    <div class="settings-scroll">
      <div class="settings-section" data-sec="cats"></div>
      <div class="settings-section" data-sec="users">
        <h3>Benutzer</h3>
        <div data-list="users"></div>
        <div class="cat-add">
          <input type="text" data-f="u" placeholder="Benutzername" autocomplete="off" />
          <input type="password" data-f="p" placeholder="Passwort (min. 6)" autocomplete="new-password" />
          <button type="button" class="btn sm primary" data-a="add">Anlegen</button>
        </div>
      </div>
      <div class="settings-section" data-sec="backup">
        <h3>Backup</h3>
        <p class="field-hint" style="margin:0 0 10px;">
          Lädt alle Foyers (Objekte, Flächen, Kategorien, Gebäude) als eine JSON-Datei herunter.
          Grundriss- und Referenzbilder sind nicht enthalten — die werden über den Server gesichert
          (siehe DEPLOY.md).
        </p>
        <button type="button" class="btn sm" data-a="backup">Backup herunterladen</button>
      </div>
    </div>`;

  renderCategorySettings(container.querySelector<HTMLElement>('[data-sec="cats"]')!);

  container.querySelector('[data-a="backup"]')!.addEventListener("click", () => {
    const a = document.createElement("a");
    a.href = api.backupUrl();
    a.download = `foyerplaner-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  });

  const sec = container.querySelector<HTMLElement>('[data-sec="users"]')!;
  const listEl = sec.querySelector<HTMLElement>('[data-list="users"]')!;

  const refresh = async () => {
    let users: Array<{ id: string; username: string }> = [];
    try {
      users = await api.listUsers();
    } catch {
      listEl.innerHTML = '<div class="empty-note">Benutzerliste nicht verfügbar.</div>';
      return;
    }
    listEl.replaceChildren();
    for (const u of users) {
      const row = document.createElement("div");
      row.className = "cat-row";
      row.innerHTML = `
        <span class="cat-label">${esc(u.username)}${u.username === currentUsername ? " (du)" : ""}</span>
        <button type="button" class="btn sm ghost" data-a="pw">Passwort…</button>
        <button type="button" class="btn sm ghost" data-a="del" style="color:var(--danger)"${
          users.length <= 1 ? " disabled" : ""
        }>Löschen</button>`;
      row.querySelector('[data-a="pw"]')!.addEventListener("click", async () => {
        const pw = await textPrompt(`Neues Passwort für „${u.username}"`, "", {
          label: "Passwort (min. 6 Zeichen)",
          confirmLabel: "Speichern",
        });
        if (!pw) return;
        try {
          await api.setUserPassword(u.id, pw);
          toast("Passwort geändert.");
        } catch (err) {
          toast(err instanceof Error ? err.message : "Fehlgeschlagen.");
        }
      });
      row.querySelector('[data-a="del"]')!.addEventListener("click", async () => {
        const ok = await confirmDialog("Benutzer löschen", `„${u.username}" wirklich löschen?`, {
          confirmLabel: "Löschen",
        });
        if (!ok) return;
        try {
          await api.deleteUser(u.id);
          await refresh();
        } catch (err) {
          toast(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
        }
      });
      listEl.appendChild(row);
    }
  };

  sec.querySelector('[data-a="add"]')!.addEventListener("click", async () => {
    const u = sec.querySelector<HTMLInputElement>('[data-f="u"]')!;
    const p = sec.querySelector<HTMLInputElement>('[data-f="p"]')!;
    try {
      await api.createUser(u.value.trim(), p.value);
      u.value = "";
      p.value = "";
      await refresh();
      toast("Benutzer angelegt.");
    } catch (err) {
      toast(err instanceof Error ? err.message : "Anlegen fehlgeschlagen.");
    }
  });

  void refresh();
}

/** Open the settings in a modal dialog (used from the project overview). */
export function openSettingsDialog(currentUsername: string): void {
  const dlg = document.createElement("dialog");
  dlg.className = "dialog dialog-wide";
  dlg.innerHTML = `
    <div class="dialog-body">
      <div class="dialog-head">
        <h2>Einstellungen</h2>
        <button type="button" class="btn ghost sm" data-a="close">Schließen</button>
      </div>
      <div data-slot="settings"></div>
    </div>`;
  document.body.appendChild(dlg);
  dlg.addEventListener("close", () => dlg.remove());
  dlg.querySelector('[data-a="close"]')!.addEventListener("click", () => dlg.close());
  renderSettingsInto(dlg.querySelector<HTMLElement>('[data-slot="settings"]')!, currentUsername);
  dlg.showModal();
}
